import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pencil, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/lib/supabase";
import type { RecordingOpts } from "@/components/HomeFeed";
import type { CardContext } from "@/types/cardContext";
import MemoryDetailSheet from "@/components/MemoryDetailSheet";
import CollectionEditSheet from "@/components/CollectionEditSheet";
import { invalidateMemories } from "@/hooks/useMemories";
import {
  cancelCollection,
  finishCollection,
  invalidateCollections,
  reopenCollection,
  useCollections,
} from "@/hooks/useCollections";
import { useAuth } from "@/hooks/useAuth";
import { useChildren } from "@/hooks/useChildren";
import { useEntitlements } from "@/hooks/useEntitlements";
import StepRow from "@/components/collection/StepRow";
import LockedFeatureSheet from "@/components/LockedFeatureSheet";
import { toast } from "sonner";
import {
  useCollectionDetail,
  type EnrichedStep,
} from "@/hooks/queries/useCollectionDetail";
import { useRecentMemories } from "@/hooks/queries/useRecentMemories";

type Tab = "moments" | "memories";

interface Props {
  collectionId: string;
  collectionName: string;
  collectionIcon: string;
  collectionColor: string;
  entryCount: number;
  templateId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onRecord: (opts?: RecordingOpts) => void;
  /** Controls which tab is shown first. Defaults to "moments". */
  defaultTab?: Tab;
  /** Optional card context for template prompts. */
  cardContext?: CardContext;
  /** Optional participant connection IDs — restricts who can be tagged
   *  on memories captured from this collection. Empty = no restriction. */
  collectionParticipantConnectionIds?: string[];
  /** Child IDs the collection primarily concerns. */
  collectionParticipantChildIds?: string[];
  /** Called after the edit sheet successfully saves — use to bust the collections cache. */
  onCollectionUpdated?: (updated: {
    name: string;
    icon: string;
    participantConnectionIds: string[];
    participantChildIds: string[];
  }) => void;
  /** Collection mode: 'open' (default) | 'guided' | 'hybrid'. */
  collectionMode?: "open" | "guided" | "hybrid";
  /** Set when all required steps are complete. */
  completedAt?: string | null;
  /** Children IDs permanently locked to this collection — no remove button. */
  lockedChildIds?: string[];
  /** Space-member IDs permanently locked to this collection — no remove button. */
  lockedMemberIds?: string[];
  /** When true, show the "collection is ready" welcome banner at the top. */
  isNew?: boolean;
  /** Called after a Finish or Cancel action so the parent can refresh. */
  onCollectionChanged?: () => void;
}

const MEMORY_TYPE_ICONS: Record<string, string> = {
  voice: "\uD83C\uDFA4",
  photo: "\uD83D\uDCF7",
  drawing: "\uD83C\uDFA8",
  written: "\u270D\uFE0F",
  shortie: "\uD83C\uDFAC",
};

export default function CollectionDetailSheet({
  collectionId,
  collectionName,
  collectionIcon,
  collectionColor,
  entryCount,
  templateId,
  isOpen,
  onClose,
  onRecord,
  defaultTab = "moments",
  cardContext,
  collectionParticipantConnectionIds,
  collectionParticipantChildIds,
  onCollectionUpdated,
  collectionMode = "open",
  completedAt,
  lockedChildIds = [],
  lockedMemberIds = [],
  isNew = false,
  onCollectionChanged,
}: Props) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { children } = useChildren();
  const { activeCollection, startActiveMode, stopActiveMode } = useCollections();
  const { canActiveCapture } = useEntitlements();
  const [activeCaptureSheetOpen, setActiveCaptureSheetOpen] = useState(false);

  const { entries, enrichedSteps, isLoading: loading, refetch: refetchDetail } = useCollectionDetail(collectionId, templateId, isOpen);
  const [activeTab, setActiveTab] = useState<Tab>(defaultTab);
  const [detailMemoryId, setDetailMemoryId] = useState<string | null>(null);
  const [showPicker, setShowPicker] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [finishDialogOpen, setFinishDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [switchActiveDialogOpen, setSwitchActiveDialogOpen] = useState(false);
  const [lifecycleBusy, setLifecycleBusy] = useState(false);
  const [activeModeBusy, setActiveModeBusy] = useState(false);

  // Local copies so edits from the edit sheet reflect immediately
  const [localName, setLocalName] = useState(collectionName);
  const [localIcon, setLocalIcon] = useState(collectionIcon);
  const [connectionIds, setConnectionIds] = useState<string[]>(
    collectionParticipantConnectionIds ?? [],
  );
  const [childIds, setChildIds] = useState<string[]>(
    collectionParticipantChildIds ?? [],
  );

  // Build a quick name-lookup map from children
  const childNameMap = useMemo(() => {
    const m: Record<string, string> = {};
    for (const c of children) m[c.id] = c.name;
    return m;
  }, [children]);

  // Sync from props when the sheet opens for a different collection, and reset tab
  useEffect(() => {
    setLocalName(collectionName);
    setLocalIcon(collectionIcon);
    setConnectionIds(collectionParticipantConnectionIds ?? []);
    setChildIds(collectionParticipantChildIds ?? []);
  }, [collectionId, collectionName, collectionIcon, collectionParticipantConnectionIds, collectionParticipantChildIds]);

  useEffect(() => {
    if (isOpen) setActiveTab(defaultTab);
  }, [isOpen, defaultTab]);

  // ── Derived values ───────────────────────────────────────────────────────────

  const isTemplateCollection = !!templateId;

  // Step progress
  const requiredSteps = enrichedSteps.filter((s) => s.required);
  const doneSteps = requiredSteps.filter((s) => s.completion);
  const isCompleted = !!completedAt;
  const hasSteps = enrichedSteps.length > 0;
  const progressPercent = requiredSteps.length > 0 ? (doneSteps.length / requiredSteps.length) * 100 : 0;

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleStepCapture = (step: EnrichedStep) => {
    if (!step.content_library) return;
    const card = step.content_library;
    const captureType =
      (card.capture_type as "voice" | "drawing" | "photo" | "written" | "video") ??
      "voice";
    const ctx: CardContext = {
      contentLibraryId: card.id,
      title: step.step_title_override ?? card.title ?? localName,
      body: card.body,
      instructions: card.instructions ?? undefined,
      tip: card.tip ?? undefined,
      captureType,
      tags: card.tags ?? [],
      cardType: card.card_type,
      voiceRequired: card.voice_required,
    };
    onClose();
    onRecord({
      contextPrompt: step.step_title_override ?? card.title ?? localName,
      captureType,
      cardContext: ctx,
      voiceRequired: card.voice_required,
      collectionId: collectionId,
      collectionIcon: localIcon,
      collectionColor: collectionColor,
      collectionParticipantConnectionIds: connectionIds,
    });
  };

  const handleAddEntry = () => {
    onClose();
    onRecord({
      contextPrompt: localName,
      collectionId: collectionId,
      collectionIcon: localIcon,
      collectionColor: collectionColor,
      collectionParticipantConnectionIds: connectionIds,
    });
  };

  const refreshEntries = () => {
    void refetchDetail();
    void invalidateCollections();
    void invalidateMemories();
  };

  const removeChild = async (childId: string) => {
    const newIds = childIds.filter((id) => id !== childId);
    setChildIds(newIds);
    await supabase
      .from("collections")
      .update({ participant_child_ids: newIds })
      .eq("id", collectionId);
    void invalidateCollections();
  };

  const removeMember = async (memberId: string) => {
    const newIds = connectionIds.filter((id) => id !== memberId);
    setConnectionIds(newIds);
    await supabase
      .from("collections")
      .update({ participant_member_ids: newIds })
      .eq("id", collectionId);
    void invalidateCollections();
  };

  const handleFinish = async () => {
    if (!user || lifecycleBusy) return;
    setLifecycleBusy(true);
    const { error } = await finishCollection(user.id, collectionId);
    setLifecycleBusy(false);
    setFinishDialogOpen(false);
    if (error) {
      toast.error(t("collections.lifecycle.finishError"));
      return;
    }
    toast.success(t("collections.lifecycle.finishSuccess"));
    onCollectionChanged?.();
    onClose();
  };

  const handleReopen = async () => {
    if (!user || lifecycleBusy) return;
    setLifecycleBusy(true);
    const { error } = await reopenCollection(user.id, collectionId);
    setLifecycleBusy(false);
    if (error) {
      toast.error(t("collections.lifecycle.reopenError"));
      return;
    }
    toast.success(t("collections.lifecycle.reopenSuccess"));
    onCollectionChanged?.();
    onClose();
  };

  const handleCancel = async () => {
    if (!user || lifecycleBusy) return;
    setLifecycleBusy(true);
    const { error } = await cancelCollection(user.id, collectionId);
    setLifecycleBusy(false);
    setCancelDialogOpen(false);
    if (error) {
      toast.error(t("collections.lifecycle.cancelError"));
      return;
    }
    toast.success(t("collections.lifecycle.cancelSuccess"));
    onCollectionChanged?.();
    onClose();
  };

  // ── Active-mode toggle ────────────────────────────────────────────────────────
  const isCurrentlyActive = activeCollection?.id === collectionId;
  const otherActiveCollection =
    activeCollection && activeCollection.id !== collectionId ? activeCollection : null;

  const runStartActiveMode = async () => {
    if (activeModeBusy) return;
    setActiveModeBusy(true);
    try {
      await startActiveMode(collectionId);
      toast.success(t("collections.detail.activeModeStartedToast"));
    } catch (err) {
      console.error("[CollectionDetailSheet] startActiveMode failed", err);
      toast.error(t("collections.detail.activeModeError"));
    } finally {
      setActiveModeBusy(false);
      setSwitchActiveDialogOpen(false);
    }
  };

  const handleActiveModeToggle = () => {
    // Only entering active mode is gated — turning it OFF remains available
    // even if the user downgraded after starting.
    if (!isCurrentlyActive && !canActiveCapture) {
      setActiveCaptureSheetOpen(true);
      return;
    }
    if (isCurrentlyActive) {
      void (async () => {
        if (activeModeBusy) return;
        setActiveModeBusy(true);
        try {
          await stopActiveMode(collectionId);
          toast.success(t("collections.detail.activeModeStoppedToast"));
        } catch (err) {
          console.error("[CollectionDetailSheet] stopActiveMode failed", err);
          toast.error(t("collections.detail.activeModeError"));
        } finally {
          setActiveModeBusy(false);
        }
      })();
      return;
    }
    if (otherActiveCollection) {
      setSwitchActiveDialogOpen(true);
      return;
    }
    void runStartActiveMode();
  };

  const displayMemoryCount = loading ? entryCount : entries.length;

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <>
      <Sheet open={isOpen} onOpenChange={(v) => { if (!v) onClose(); }}>
        <SheetContent
          side="bottom"
          className="rounded-t-3xl max-h-[85dvh] overflow-y-auto overflow-x-hidden px-5 pt-5 pb-8"
          hideCloseButton
        >
          <SheetTitle className="sr-only">{localName}</SheetTitle>

          {/* ── Header ─────────────────────────────────────────────────────── */}
          <div className="px-1 pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span style={{ fontSize: 24 }}>{localIcon}</span>
                <div>
                  <p className="text-base font-medium text-foreground">{localName}</p>
                  <p className="text-xs text-muted-foreground">
                    {t("collections.entry", { count: displayMemoryCount })}
                  </p>
                </div>
              </div>

              {/* Header actions: edit + close */}
              <div className="flex items-center gap-0.5">
                <button
                  type="button"
                  onClick={() => setEditOpen(true)}
                  className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  aria-label={t("aria.editCollection")}
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <SheetClose asChild>
                  <button
                    type="button"
                    className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    aria-label="Close"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </SheetClose>
              </div>
            </div>

            {/* Participant chips — children and members with lock/remove */}
            {(childIds.length > 0 || connectionIds.length > 0) && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {childIds.map((childId) => {
                  const name = childNameMap[childId] ?? "Child";
                  const locked = lockedChildIds.includes(childId);
                  return (
                    <span
                      key={childId}
                      title={
                        locked
                          ? t("collections.step.locked.tooltip", { name })
                          : undefined
                      }
                      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium bg-muted text-muted-foreground"
                    >
                      {name}
                      {locked ? (
                        <span className="text-[10px]" aria-label="Locked">🔒</span>
                      ) : (
                        <button
                          type="button"
                          aria-label={t("aria.removeMember", { name })}
                          className="ml-0.5 leading-none text-muted-foreground/60 hover:text-foreground transition-colors"
                          onClick={() => void removeChild(childId)}
                        >
                          ×
                        </button>
                      )}
                    </span>
                  );
                })}
                {connectionIds.map((memberId, i) => {
                  const locked = lockedMemberIds.includes(memberId);
                  return (
                    <span
                      key={memberId}
                      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium bg-muted text-muted-foreground"
                    >
                      👤 {i + 1}
                      {locked ? (
                        <span className="text-[10px]" aria-label="Locked">🔒</span>
                      ) : (
                        <button
                          type="button"
                          aria-label={t("aria.close")}
                          className="ml-0.5 leading-none text-muted-foreground/60 hover:text-foreground transition-colors"
                          onClick={() => void removeMember(memberId)}
                        >
                          ×
                        </button>
                      )}
                    </span>
                  );
                })}
              </div>
            )}

            {/* Progress bar — template collections with required steps */}
            {isTemplateCollection && requiredSteps.length > 0 && (
              <div
                className="mt-2.5 w-full overflow-hidden"
                style={{ height: 3, borderRadius: 2, backgroundColor: `${collectionColor}20` }}
              >
                <div
                  style={{
                    height: "100%",
                    borderRadius: 2,
                    width: `${progressPercent}%`,
                    backgroundColor: collectionColor,
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
            )}
          </div>

          {/* ── Completion banner ──────────────────────────────────────────── */}
          {isCompleted && (
            <div className="mb-4 rounded-xl border border-green-200/60 dark:border-green-900/40 bg-green-50/60 dark:bg-green-950/20 px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground">
                    {t("collections.completed.heading")}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t("collections.completed.subtext")}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t("collections.completed.date", {
                      date: new Date(completedAt as string).toLocaleDateString(undefined, {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      }),
                    })}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleReopen}
                  disabled={lifecycleBusy}
                  className="shrink-0 text-xs font-medium text-foreground/80 hover:text-foreground underline-offset-2 hover:underline disabled:opacity-50"
                >
                  {t("collections.lifecycle.reopen")}
                </button>
              </div>
            </div>
          )}

          {/* ── Welcome banner — shown once on a freshly created collection ─── */}
          {isNew && (
            <div
              className="mb-4 rounded-xl px-4 py-3"
              style={{ backgroundColor: `${collectionColor}14`, border: `1px solid ${collectionColor}30` }}
            >
              <p className="text-sm font-semibold text-foreground">
                {t(collectionMode === "guided" || collectionMode === "hybrid"
                  ? "collections.create.welcome.guided"
                  : "collections.create.welcome.open")}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {t(collectionMode === "guided" || collectionMode === "hybrid"
                  ? "collections.create.welcome.guidedSubtext"
                  : "collections.create.welcome.openSubtext")}
              </p>
            </div>
          )}

          {/* ── Tab bar — all template collections with steps ─────────────── */}
          {hasSteps && (
            <div
              className="flex mb-4"
              style={{
                backgroundColor: `${collectionColor}12`,
                borderRadius: 10,
                padding: 3,
              }}
            >
              <button
                type="button"
                className="flex-1 py-1.5 text-xs font-semibold rounded-[8px] transition-all"
                style={
                  activeTab === "moments"
                    ? { backgroundColor: collectionColor, color: "#fff" }
                    : { color: collectionColor, opacity: 0.65 }
                }
                onClick={() => setActiveTab("moments")}
              >
                {t("collections.detail.tabs.moments")}{enrichedSteps.length > 0 ? ` (${enrichedSteps.length})` : ""}
              </button>
              <button
                type="button"
                className="flex-1 py-1.5 text-xs font-semibold rounded-[8px] transition-all"
                style={
                  activeTab === "memories"
                    ? { backgroundColor: collectionColor, color: "#fff" }
                    : { color: collectionColor, opacity: 0.65 }
                }
                onClick={() => setActiveTab("memories")}
              >
                {t("collections.detail.tabs.memories")}{entries.length > 0 ? ` (${entries.length})` : ""}
              </button>
            </div>
          )}

          {/* ── Loading skeleton ───────────────────────────────────────────── */}
          {loading ? (
            <div className="space-y-3 pt-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 rounded-xl bg-muted animate-pulse" />
              ))}
            </div>
          ) : (
            <>
              {/* ── Steps section — all template collections with steps ──────── */}
              {hasSteps && activeTab === "moments" && (
                <div className="mb-4">
                  {/* Step progress: pill indicators + text */}
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex gap-1">
                      {requiredSteps.map((step) => (
                        <div
                          key={step.id}
                          className="h-2 w-2 rounded-full"
                          style={{
                            backgroundColor: step.completion
                              ? collectionColor
                              : `${collectionColor}30`,
                          }}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t("collections.progress", {
                        done: doneSteps.length,
                        total: requiredSteps.length,
                      })}
                    </p>
                  </div>

                  <div className="space-y-2">
                    {enrichedSteps.map((step) => (
                      <StepRow
                        key={step.id}
                        step={step}
                        completed={!!step.completion}
                        completion={step.completion ?? undefined}
                        onCapture={() => handleStepCapture(step)}
                        onViewMemory={setDetailMemoryId}
                        collectionColor={collectionColor}
                        disabled={isCompleted}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* ── Memories section — when on memories tab, or no steps ─── */}
              {(activeTab === "memories" || !hasSteps) && (

                <>
                  {entries.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      {isTemplateCollection
                        ? t("collections.detail.tapPrompt")
                        : t("collections.detail.noEntries")}
                    </p>
                  ) : (
                    <div className="space-y-2 pt-1">
                      {entries.map((entry) => {
                        const mem = entry.memory;
                        if (!mem) return null;
                        const typeIcon = MEMORY_TYPE_ICONS[mem.memory_type] ?? "\uD83D\uDD25";
                        const excerpt = mem.ai_chapter || mem.raw_transcript || "";
                        const truncated =
                          excerpt.length > 80
                            ? excerpt.slice(0, 77) + "\u2026"
                            : excerpt;

                        return (
                          <button
                            key={entry.id}
                            onClick={() => setDetailMemoryId(mem.id)}
                            className="w-full text-left rounded-xl border border-border/60 bg-card px-4 py-3 transition-colors hover:bg-muted/30"
                          >
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <span>{typeIcon}</span>
                              <span>
                                {new Date(mem.created_at).toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })}
                              </span>
                              {entry.added_by === "manual" && (
                                <span className="text-[10px] text-muted-foreground/60">
                                  {t("collections.detail.addedManually")}
                                </span>
                              )}
                            </div>
                            {mem.title && (
                              <p className="text-sm font-medium text-foreground mt-1 truncate">
                                {mem.title}
                              </p>
                            )}
                            {truncated && (
                              <p
                                className="text-xs text-muted-foreground mt-0.5"
                                style={{
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                }}
                              >
                                {truncated}
                              </p>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* ── Bottom actions — hidden in guided mode ─────────────────────── */}
          {collectionMode !== "guided" && !isCompleted && (
            <div className="flex gap-2 pt-4 pb-2">
              <Button
                className="flex-1 min-w-0 h-auto min-h-10 whitespace-normal px-3 py-2 text-center leading-tight"
                onClick={handleAddEntry}
                style={{ backgroundColor: collectionColor }}
              >
                {t("collections.detail.addEntry")}
              </Button>
              <Button
                variant="outline"
                className="flex-1 min-w-0 h-auto min-h-10 whitespace-normal px-3 py-2 text-center leading-tight"
                onClick={() => setShowPicker(true)}
              >
                {t("collections.detail.addExisting")}
              </Button>
            </div>
          )}

          {/* ── Active-capture toggle ───────────────────────────────────────── */}
          {!isCompleted && (
            <div className="pt-3">
              <Button
                variant={isCurrentlyActive ? "secondary" : "outline"}
                className="w-full"
                onClick={handleActiveModeToggle}
                disabled={activeModeBusy}
                style={
                  isCurrentlyActive
                    ? { borderColor: `${collectionColor}60`, backgroundColor: `${collectionColor}18`, color: collectionColor }
                    : undefined
                }
              >
                {isCurrentlyActive
                  ? t("collections.detail.stopActiveMode")
                  : t("collections.detail.startActiveMode")}
              </Button>
            </div>
          )}

          {/* ── Lifecycle actions — Finish (if active) + Cancel ─────────────── */}
          <div className="flex items-center justify-center gap-4 pt-3 pb-2 text-xs">
            {!isCompleted && (
              <>
                <button
                  type="button"
                  onClick={() => setFinishDialogOpen(true)}
                  className="text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
                >
                  {t("collections.lifecycle.finish")}
                </button>
                <span className="text-muted-foreground/30" aria-hidden>·</span>
              </>
            )}
            <button
              type="button"
              onClick={() => setCancelDialogOpen(true)}
              className="text-destructive/70 hover:text-destructive underline-offset-2 hover:underline"
            >
              {t("collections.lifecycle.cancel")}
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {/* ── Finish confirmation ───────────────────────────────────────────── */}
      <AlertDialog open={finishDialogOpen} onOpenChange={setFinishDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("collections.lifecycle.finishTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("collections.lifecycle.finishBody")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={lifecycleBusy}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleFinish} disabled={lifecycleBusy}>
              {t("collections.lifecycle.finishConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Switch active-mode confirmation ──────────────────────────────── */}
      <AlertDialog open={switchActiveDialogOpen} onOpenChange={setSwitchActiveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("collections.detail.switchActiveModeTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("collections.detail.switchActiveModeBody", {
                other: otherActiveCollection?.name ?? "",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={activeModeBusy}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void runStartActiveMode()}
              disabled={activeModeBusy}
            >
              {t("collections.detail.switchActiveModeConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Cancel (delete collection) confirmation ───────────────────────── */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("collections.lifecycle.cancelTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("collections.lifecycle.cancelBody")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={lifecycleBusy}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              disabled={lifecycleBusy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("collections.lifecycle.cancelConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Memory detail sheet */}
      {detailMemoryId && (
        <MemoryDetailSheet
          memoryId={detailMemoryId}
          isOpen={!!detailMemoryId}
          onClose={() => setDetailMemoryId(null)}
        />
      )}

      {/* Memory picker */}
      {showPicker && (
        <MemoryPicker
          collectionId={collectionId}
          existingMemoryIds={entries
            .map((e) => e.memory?.id)
            .filter((id): id is string => !!id)}
          onClose={() => setShowPicker(false)}
          onAdded={refreshEntries}
        />
      )}

      {/* Collection edit sheet */}
      <CollectionEditSheet
        collectionId={collectionId}
        name={localName}
        icon={localIcon}
        color={collectionColor}
        participantConnectionIds={connectionIds}
        participantChildIds={childIds}
        lockedChildIds={lockedChildIds}
        lockedMemberIds={lockedMemberIds}
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        onSaved={(updated) => {
          setLocalName(updated.name);
          setLocalIcon(updated.icon);
          setConnectionIds(updated.participantConnectionIds);
          setChildIds(updated.participantChildIds);
          onCollectionUpdated?.(updated);
        }}
      />

      <LockedFeatureSheet
        feature="active_capture"
        open={activeCaptureSheetOpen}
        onClose={() => setActiveCaptureSheetOpen(false)}
      />
    </>
  );
}

// ── Memory Picker ───────────────────────────────────────────────────────────

function MemoryPicker({
  collectionId,
  existingMemoryIds,
  onClose,
  onAdded,
}: {
  collectionId: string;
  existingMemoryIds: string[];
  onClose: () => void;
  onAdded: () => void;
}) {
  const { t } = useTranslation();
  const { data: memories, isLoading: loading } = useRecentMemories();
  const [search, setSearch] = useState("");

  const filtered = memories.filter((m) => {
    if (existingMemoryIds.includes(m.id)) return false;
    if (search && m.title && !m.title.toLowerCase().includes(search.toLowerCase()))
      return false;
    return true;
  });

  const handleSelect = async (memoryId: string) => {
    const { error } = await supabase.from("collection_memories").upsert(
      { collection_id: collectionId, memory_id: memoryId, added_by: "manual" },
      { onConflict: "collection_id,memory_id", ignoreDuplicates: true },
    );
    if (error) {
      console.error("[CollectionDetailSheet] collection_memories upsert failed", error);
      toast.error(t("collections.detail.picker.addError"));
      return;
    }
    toast.success(t("collections.detail.picker.added"));
    void invalidateCollections();
    onAdded();
    onClose();
  };

  return (
    <Sheet open onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="bottom" className="rounded-t-3xl h-[70vh] overflow-y-auto overflow-x-hidden px-5">
        <SheetTitle className="sr-only">{t("collections.detail.picker.title")}</SheetTitle>
        <div className="space-y-3">
          <p className="text-sm font-medium text-foreground">
            {t("collections.detail.picker.title")}
          </p>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("collections.detail.picker.search")}
            className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-12 rounded-lg bg-muted animate-pulse" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-6">
              {t("collections.detail.picker.empty")}
            </p>
          ) : (
            <div className="space-y-1.5">
              {filtered.map((m) => (
                <button
                  key={m.id}
                  onClick={() => handleSelect(m.id)}
                  className="w-full text-left rounded-lg border border-border/40 bg-card px-3 py-2.5 text-sm hover:bg-muted/30 transition-colors"
                >
                  <p className="font-medium text-foreground truncate">
                    {m.title || t("collections.detail.picker.untitled")}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(m.created_at).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
